import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { GetPatientsBalanceResponse_PatientBalance } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { useAppScopeStore } from '@frontend/scope';
import { Table } from '@frontend/design-system';
import { useGenerateColumns } from '../../../../../hooks/use-generate-columns';
import { CollectionColumn, generateColumns } from '../../../common/generate-columns';
import { useValidateUsersShallowStore } from '../../stores/validate-users-store';
import { usePreviewPagination } from './hooks/use-preview-pagination';
import { RowCheckbox } from './row-checkbox';

type DesktopListProps = {
  patients: GetPatientsBalanceResponse_PatientBalance[];
  onSelectionChange: Dispatch<SetStateAction<Record<string, boolean>>>;
  selected: Record<string, boolean>;
  useNewApi: boolean;
};

export const DesktopList = ({ patients, onSelectionChange, selected, useNewApi }: DesktopListProps) => {
  const { selectedLocationIds } = useAppScopeStore();
  const original_columns = useGenerateColumns<CollectionColumn>(generateColumns, false, undefined, {
    hideLocationNameColumn: selectedLocationIds.length === 1,
  });
  const { excluded, toggleExcluded } = useValidateUsersShallowStore('excluded', 'toggleExcluded');

  const {
    currentPage,
    data,
    goNext,
    goPrev,
    hasNextPage,
    hasPreviousPage,
    isLoading: fetchingPage,
    pageSize,
    setPageSize,
    setOrderBy,
  } = usePreviewPagination({ enabled: useNewApi ?? false });

  const columns = useMemo(() => {
    if (useNewApi) {
      return [
        {
          id: 'id',
          Header: '',
          accessor: (bal: CollectionColumn) => bal,
          width: 50,
          disableSortBy: true,
          cellRenderer: (balance: CollectionColumn) => (
            <RowCheckbox
              isSelected={!!balance.phoneNumber && !excluded.includes(balance.patientId)}
              onChange={() => toggleExcluded(balance.patientId)}
              isDisabled={!balance.phoneNumber}
            />
          ),
        },
        ...original_columns,
      ];
    }
    return original_columns;
  }, [useNewApi, excluded.length]);

  const handleSort = useCallback((res: { id: string; value: 'asc' | 'desc' }[]) => {
    if (!res.length) {
      return;
    }
    const { id, value } = res[0] ?? {};
    if (id) {
      setOrderBy(`${id} ${value}`);
    }
  }, []);

  const paginationConfig = useMemo(() => {
    if (useNewApi) {
      // When using the new API, we want to use manual pagination so that we can control the data fetching.
      return {
        manualPaginationConfig: {
          page: currentPage,
          hasNext: !!hasNextPage,
          hasPrevious: hasPreviousPage,
          onNumRowsChange: (num: number) => {
            setPageSize(num);
          },
          defaultRowsPerPage: pageSize,
          rowsPerPageOptions: [10, 20, 50, 100],
          handleChange: (prevOrNext: 'prev' | 'next') => {
            if (prevOrNext === 'prev') goPrev();
            if (prevOrNext === 'next') goNext();
          },
        },
      };
    }
    return {
      clientPaginationConfig: {
        defaultRowsPerPage: 10,
        rowsPerPageOptions: [10, 20, 50, 100],
        showNumRowSelector: true,
      },
    };
  }, [useNewApi, currentPage, hasNextPage, hasPreviousPage, pageSize, setPageSize, goPrev, goNext]);

  const rowSelectionConfig = useMemo(() => {
    if (useNewApi) {
      // When using the new API, we don't want table component to manage the selection for rows.
      // it will be managed by <RowSelect /> component.
      return undefined;
    }
    return {
      hideBulkSelection: true,
      isRowDisabled: ({ locationIds, phoneNumber }: { locationIds: string[]; phoneNumber?: string }) =>
        locationIds?.length > 1 || !phoneNumber,
      onSelectionToggle: (_selectedRows: any, _data: any, selectedIds: SetStateAction<Record<string, boolean>>) => {
        onSelectionChange(selectedIds);
      },
      initialState: selected,
    };
  }, [useNewApi]);

  return (
    <Table
      isLoading={useNewApi ? fetchingPage : undefined}
      keepPreviousDataWhileLoading={true}
      allowMobileView={false}
      fullHeight={true}
      fullHeightConfig={{
        maxHeight: 400,
        minHeight: 300,
      }}
      //@ts-ignore-next-line
      colConfig={columns.filter((col) => col.id !== 'actions')}
      data={useNewApi ? data : patients}
      hasResponsiveColWidths
      isSelectable={!useNewApi}
      uniqueRowId={(row) => row.patientId}
      rowSelectionConfig={rowSelectionConfig}
      isPaginated
      {...paginationConfig}
      onSortChange={useNewApi ? handleSort : undefined}
    />
  );
};
